import type {RefObject} from "react";
import {useCallback, useLayoutEffect, useRef} from "react";
import type {SpaceViewRenderer} from "../logic3d/renderers/SpaceViewRenderer";
import type {ISignal} from "../../../../../utils/signal/ISignal";
import {THREEUtils} from "../../../../../utils/THREEUtils";
import {HTMLUtils} from "../../../../../utils/HTML/HTMLUtils";

export interface IWorldPosition {
	x: number;
	y: number;
}

export interface IActionBarSemanticState {
	isOpen: boolean;
	isLinkable: boolean;
	isEditable: boolean;
	isInEditMode: boolean;
}

interface IFloatingOverlayStyleParams {
	readonly spaceViewRenderer: SpaceViewRenderer;
	readonly getWorldPosition?: () => IWorldPosition;
	readonly worldX?: number;
	readonly worldY?: number;
	readonly position?: "top" | "bottom" | "center";
	readonly clampToCanvas?: boolean;
	readonly element?: Element;
	readonly elementSize?: {width: number; height: number};
}

interface IFloatingOverlayPositionOptions {
	readonly elementRef: RefObject<HTMLElement>;
	readonly spaceViewRenderer: SpaceViewRenderer;
	readonly getWorldPosition: () => IWorldPosition;
	readonly position?: "top" | "bottom" | "center";
	readonly clampToCanvas?: boolean;
	readonly enabled?: boolean;
	readonly signals?: ISignal[];
	readonly watchCamera?: boolean;
	readonly watchRender?: boolean;
	readonly watchCanvasResize?: boolean;
}

const EMPTY_SIGNALS: ISignal[] = [];

export const areActionBarSemanticStatesEqual = (a: IActionBarSemanticState, b: IActionBarSemanticState) => {
	return a.isOpen === b.isOpen && a.isLinkable === b.isLinkable && a.isEditable === b.isEditable && a.isInEditMode === b.isInEditMode;
};

export const resolveFloatingWorldPosition = (getWorldPosition?: () => IWorldPosition, worldX: number = 0, worldY: number = 0): IWorldPosition => {
	return getWorldPosition?.() ?? {x: worldX, y: worldY};
};

export const getFloatingOverlayStyle = ({
	spaceViewRenderer,
	getWorldPosition,
	worldX = 0,
	worldY = 0,
	position = "center",
	clampToCanvas = true,
	element,
	elementSize,
}: IFloatingOverlayStyleParams) => {
	const resolvedWorldPosition = resolveFloatingWorldPosition(getWorldPosition, worldX, worldY);

	return THREEUtils.getStyleForFloatingUIElement(
		resolvedWorldPosition.x,
		resolvedWorldPosition.y,
		spaceViewRenderer.spaceOffset.z,
		spaceViewRenderer,
		clampToCanvas,
		position,
		element,
		elementSize,
	);
};

export const useFloatingOverlayPosition = ({
	elementRef,
	spaceViewRenderer,
	getWorldPosition,
	position = "center",
	clampToCanvas = true,
	enabled = true,
	signals = EMPTY_SIGNALS,
	watchCamera = false,
	watchRender = true,
	watchCanvasResize = true,
}: IFloatingOverlayPositionOptions) => {
	const animationFrameId = useRef<number | null>(null);
	const observedElementRef = useRef<HTMLElement | null>(null);
	const resizeObserverRef = useRef<ResizeObserver | null>(null);
	const latestOptions = useRef({
		elementRef,
		spaceViewRenderer,
		getWorldPosition,
		position,
		clampToCanvas,
		enabled,
	});

	latestOptions.current = {
		elementRef,
		spaceViewRenderer,
		getWorldPosition,
		position,
		clampToCanvas,
		enabled,
	};

	const measureElementSize = useCallback((element: HTMLElement) => HTMLUtils.getSize(element), []);

	const disconnectResizeObserver = useCallback(() => {
		resizeObserverRef.current?.disconnect();
		resizeObserverRef.current = null;
		observedElementRef.current = null;
	}, []);

	const updatePosition = useCallback(() => {
		const currentOptions = latestOptions.current;

		if (!currentOptions.enabled) {
			return;
		}

		const element = currentOptions.elementRef.current;

		if (!element) {
			return;
		}

		const elementSize = measureElementSize(element);
		const style = getFloatingOverlayStyle({
			spaceViewRenderer: currentOptions.spaceViewRenderer,
			getWorldPosition: currentOptions.getWorldPosition,
			position: currentOptions.position,
			clampToCanvas: currentOptions.clampToCanvas,
			element,
			elementSize,
		});
		const transform = typeof style.transform === "string" ? style.transform : "";

		if (element.style.transform !== transform) {
			element.style.transform = transform;
		}
	}, [measureElementSize]);

	const requestPositionUpdate = useCallback(() => {
		if (!latestOptions.current.enabled || animationFrameId.current !== null) {
			return;
		}

		animationFrameId.current = requestAnimationFrame(() => {
			animationFrameId.current = null;
			updatePosition();
		});
	}, [updatePosition]);

	useLayoutEffect(() => {
		if (!enabled) {
			disconnectResizeObserver();

			return;
		}

		const element = elementRef.current;

		if (observedElementRef.current === element) {
			return;
		}

		resizeObserverRef.current?.disconnect();
		resizeObserverRef.current = null;
		observedElementRef.current = element;

		if (!element) {
			return;
		}

		if (typeof ResizeObserver !== "undefined") {
			const resizeObserver = new ResizeObserver(() => {
				requestPositionUpdate();
			});

			resizeObserver.observe(element);
			resizeObserverRef.current = resizeObserver;
		}

		updatePosition();
	});

	useLayoutEffect(() => {
		return disconnectResizeObserver;
	}, [disconnectResizeObserver]);

	useLayoutEffect(() => {
		if (!enabled) {
			return;
		}

		const {cameraControls} = spaceViewRenderer.toolManager;

		requestPositionUpdate();

		if (watchCamera) {
			cameraControls.signals.cameraPropsChange.add(requestPositionUpdate);
		}

		if (watchRender) {
			spaceViewRenderer.signals.onAfterRender.add(updatePosition);
		}

		if (watchCanvasResize) {
			spaceViewRenderer.signals.onCanvasResized.add(requestPositionUpdate);
		}

		for (const signal of signals) {
			signal.add(requestPositionUpdate);
		}

		return () => {
			if (animationFrameId.current !== null) {
				cancelAnimationFrame(animationFrameId.current);
				animationFrameId.current = null;
			}

			if (watchCamera) {
				cameraControls.signals.cameraPropsChange.remove(requestPositionUpdate);
			}

			if (watchRender) {
				spaceViewRenderer.signals.onAfterRender.remove(updatePosition);
			}

			if (watchCanvasResize) {
				spaceViewRenderer.signals.onCanvasResized.remove(requestPositionUpdate);
			}

			for (const signal of signals) {
				signal.remove(requestPositionUpdate);
			}
		};
	}, [
		clampToCanvas,
		elementRef,
		enabled,
		position,
		signals,
		spaceViewRenderer,
		watchCamera,
		watchCanvasResize,
		watchRender,
		updatePosition,
		requestPositionUpdate,
	]);
};
